// =====================================================
// CLM Types - TypeScript Interfaces
// =====================================================

// ============ ENUMS ============

export type UserRole = 'admin' | 'legal' | 'requester' | 'approver' | 'viewer';

export type ContractStatus =
  | 'draft'
  | 'in_review'
  | 'pending_approval'
  | 'approved'
  | 'active'
  | 'expiring_soon'
  | 'expired'
  | 'renewed'
  | 'terminated'
  | 'cancelled';

export type ContractType =
  | 'service'
  | 'purchase'
  | 'sale'
  | 'nda'
  | 'partnership'
  | 'employment'
  | 'rental'
  | 'custom';

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'skipped';

export type ObligationStatus = 'pending' | 'in_progress' | 'completed' | 'overdue' | 'cancelled';

export type NotificationChannel = 'email' | 'whatsapp' | 'in_app';

export type NotificationStatus = 'pending' | 'sent' | 'delivered' | 'failed' | 'read';

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

// ============ CORE ENTITIES ============

export interface Organization {
  id: string;
  name: string;
  slug: string;
  settings: OrganizationSettings;
  branding: OrganizationBranding;
  ai_enabled: boolean;
  webhooks_enabled: boolean;
  retention_days: number;
  created_at: string;
  updated_at: string;
}

export interface OrganizationSettings {
  ai?: {
    enabled: boolean;
    features: {
      extraction: boolean;
      summarization: boolean;
      risk_analysis: boolean;
      semantic_search: boolean;
      qa: boolean;
    };
  };
  notifications?: {
    email: boolean;
    whatsapp: boolean;
    in_app: boolean;
  };
  approvals?: {
    require_all_approvers: boolean;
    allow_skip: boolean;
  };
  retention?: {
    contracts_years: number;
    audit_logs_years: number;
  };
}

export interface OrganizationBranding {
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  email_from_name?: string;
}

export interface User {
  id: string;
  email: string;
  full_name: string | null;
  role?: UserRole;
  avatar_url: string | null;
  phone: string | null;
  is_active: boolean;
  is_admin?: boolean;
  org_id?: string;
  created_at: string;
  updated_at: string;
}

export interface Member {
  id: string;
  org_id: string;
  user_id: string;
  role: UserRole;
  permissions: Record<string, boolean>;
  invited_by: string | null;
  invited_at: string | null;
  joined_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;

  // Joined fields
  user?: User;
  organization?: Organization;
}

export interface Counterparty {
  id: string;
  org_id: string;
  name: string;
  legal_name: string | null;
  document_number: string | null;
  type: string | null;
  email: string | null;
  phone: string | null;
  address: Record<string, any> | null;
  contacts: Contact[];
  metadata: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Contact {
  name: string;
  role?: string;
  email?: string;
  phone?: string;
}

export interface Workflow {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  contract_types: ContractType[];
  steps: WorkflowStep[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface WorkflowStep {
  step: number;
  name: string;
  approvers: string[]; // user IDs
  type: 'sequential' | 'parallel';
  sla_hours: number;
  required: boolean;
  condition?: string; // JS expression
}

export interface Template {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  contract_type: ContractType;
  variables: TemplateVariable[];
  body_richtext: string;
  workflow_id: string | null;
  version: number;
  is_active: boolean;
  parent_version_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;

  // Joined
  workflow?: Workflow;
}

export interface TemplateVariable {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'currency' | 'select' | 'textarea';
  required: boolean;
  default_value?: any;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    options?: string[];
  };
  help_text?: string;
}

export interface Clause {
  id: string;
  org_id: string;
  title: string;
  body_richtext: string;
  category: string | null;
  tags: string[];
  risk_factors: RiskFactor[];
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Contract {
  id: string;
  org_id: string;
  title: string;
  contract_number: string | null;
  type: ContractType;
  status: ContractStatus;

  // Parties
  counterparty_id: string | null;
  counterparty_name: string | null;

  // Financials
  amount: number | null;
  currency: string;
  payment_terms: string | null;

  // Dates
  start_date: string | null;
  end_date: string | null;
  signature_date: string | null;
  notice_period_days: number;

  // Renewal
  renewal_type: string | null;
  renewal_notice_days: number;
  renewed_from_id: string | null;

  // AI Analysis
  ai_summary: string | null;
  ai_key_points: string[] | null;
  risk_score: number | null;
  risk_level: RiskLevel | null;
  risk_factors: RiskFactor[];

  // Metadata
  description: string | null;
  object: string | null;
  tags: string[];
  custom_fields: Record<string, any>;

  // Workflow
  workflow_id: string | null;
  current_version_id: string | null;

  // Ownership
  created_by: string | null;
  assigned_to: string | null;

  // Tracking
  created_at: string;
  updated_at: string;
  deleted_at: string | null;

  // Joined fields
  counterparty?: Counterparty;
  workflow?: Workflow;
  current_version?: ContractVersion;
  creator?: User;
  assignee?: User;
  versions?: ContractVersion[];
  approvals?: Approval[];
  obligations?: Obligation[];
  files?: File[];
}

export interface ContractVersion {
  id: string;
  contract_id: string;
  version_number: number;
  title: string | null;
  body_richtext: string | null;

  // Files
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  file_hash: string | null;

  // OCR & Extraction
  ocr_text: string | null;
  extracted_fields: Record<string, any>;

  // Changes tracking
  changes_from_previous: any | null;
  change_summary: string | null;

  // Metadata
  notes: string | null;
  created_by: string | null;
  created_at: string;

  // Joined
  creator?: User;
}

export interface Approval {
  id: string;
  contract_id: string;
  version_id: string | null;

  step_number: number;
  step_name: string | null;
  approver_id: string;

  status: ApprovalStatus;
  decision_notes: string | null;
  decided_at: string | null;

  // SLA
  sla_hours: number | null;
  due_at: string | null;

  created_at: string;
  updated_at: string;

  // Joined
  approver?: User;
  contract?: Contract;
  version?: ContractVersion;
}

export interface Obligation {
  id: string;
  org_id: string;
  contract_id: string;

  title: string;
  description: string | null;

  // Assignment
  owner_id: string | null;

  // Timing
  due_date: string | null;
  completed_at: string | null;

  // Recurrence
  is_recurring: boolean;
  recurrence_rule: string | null;

  // SLA & Alerts
  sla_days: number | null;
  alert_days_before: number[];

  // Status
  status: ObligationStatus;

  // Evidence
  evidence_required: boolean;
  evidence_files: any[];

  created_by: string | null;
  created_at: string;
  updated_at: string;

  // Joined
  owner?: User;
  contract?: Contract;
}

export interface Comment {
  id: string;
  org_id: string;
  contract_id: string | null;
  version_id: string | null;

  content: string;
  content_richtext: string | null;

  // Threading
  parent_id: string | null;

  // Selection
  selection_text: string | null;
  selection_range: any | null;

  // Mentions
  mentions: string[];

  // Resolution
  is_resolved: boolean;
  resolved_by: string | null;
  resolved_at: string | null;

  created_by: string | null;
  created_at: string;
  updated_at: string;

  // Joined
  creator?: User;
  replies?: Comment[];
}

export interface File {
  id: string;
  org_id: string;
  contract_id: string | null;
  version_id: string | null;
  obligation_id: string | null;

  name: string;
  original_name: string | null;
  size: number | null;
  mime_type: string | null;

  // Storage
  storage_path: string;
  storage_bucket: string;
  url: string | null;

  // Processing
  ocr_text: string | null;
  ocr_language: string;

  // Security
  file_hash: string | null;
  is_encrypted: boolean;

  // Metadata
  tags: string[];
  folder: string | null;

  // Lifecycle
  uploaded_by: string | null;
  uploaded_at: string;
  deleted_at: string | null;

  created_at: string;

  // Joined
  uploader?: User;
}

export interface Notification {
  id: string;
  org_id: string;
  user_id: string;

  channel: NotificationChannel;

  // Recipient
  to_address: string | null;

  // Content
  subject: string | null;
  body: string | null;
  payload: Record<string, any>;

  // Delivery
  status: NotificationStatus;
  sent_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  error_message: string | null;

  // Retry
  retry_count: number;
  max_retries: number;

  // Context
  entity_type: string | null;
  entity_id: string | null;

  created_at: string;
}

export interface Webhook {
  id: string;
  org_id: string;

  url: string;
  secret: string | null;

  events: string[];

  is_active: boolean;

  // Stats
  last_triggered_at: string | null;
  success_count: number;
  failure_count: number;

  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuditLog {
  id: string;
  org_id: string;

  actor_id: string | null;
  actor_email: string | null;
  actor_ip: string | null;

  action: string;

  entity_type: string;
  entity_id: string | null;

  // Changes
  before_data: any | null;
  after_data: any | null;
  diff: any | null;

  // Context
  metadata: Record<string, any>;
  user_agent: string | null;

  created_at: string;

  // Joined
  actor?: User;
}

export interface CustomField {
  id: string;
  org_id: string;

  entity_type: string;

  key: string;
  label: string;
  field_type: string;

  config: Record<string, any>;

  is_required: boolean;
  is_active: boolean;
  display_order: number | null;

  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// ============ AI TYPES ============

export interface RiskFactor {
  category: string;
  factor: string;
  level: RiskLevel;
  score: number;
  clause_reference?: string;
  recommendation?: string;
}

export interface AIExtraction {
  parties?: {
    contractor?: string;
    contracted?: string;
    [key: string]: string | undefined;
  };
  dates?: {
    start?: string;
    end?: string;
    signature?: string;
    [key: string]: string | undefined;
  };
  financial?: {
    total_amount?: number;
    currency?: string;
    payment_terms?: string;
  };
  clauses?: {
    penalty?: string;
    renewal?: string;
    notice_period?: string;
    [key: string]: string | undefined;
  };
  [key: string]: any;
}

export interface AISummary {
  executive_summary: string;
  key_points: string[];
  obligations?: Array<{
    title: string;
    due_date?: string;
    responsible?: string;
  }>;
}

export interface AIComparison {
  changes: Array<{
    type: 'addition' | 'deletion' | 'modification';
    section: string;
    before?: string;
    after?: string;
    content?: string;
    impact: 'low' | 'medium' | 'high';
    explanation?: string;
  }>;
  summary: string;
}

export interface AIAnswer {
  answer: string;
  confidence: number;
  sources: Array<{
    clause?: string;
    text: string;
  }>;
}

// ============ API TYPES ============

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

export interface ApiError {
  error: string;
  message: string;
  details?: any;
}

// ============ FORM TYPES ============

export interface ContractFormData {
  title: string;
  type: ContractType;
  counterparty_id?: string;
  counterparty_name?: string;
  amount?: number;
  currency: string;
  start_date?: string;
  end_date?: string;
  description?: string;
  tags?: string[];
  template_id?: string;
  variables?: Record<string, any>;
}

export interface ApprovalDecision {
  status: 'approved' | 'rejected';
  notes?: string;
}

// ============ DASHBOARD TYPES ============

export interface DashboardStats {
  contracts: {
    total: number;
    by_status: Record<ContractStatus, number>;
    by_type: Record<ContractType, number>;
  };
  financial: {
    total_value: number;
    receivables: number;
    payables: number;
  };
  obligations: {
    pending: number;
    overdue: number;
    completed_this_month: number;
  };
  approvals: {
    pending: number;
    pending_from_me: number;
  };
  expiring_soon: number;
}

export interface ContractTimeline {
  date: string;
  event: string;
  description: string;
  actor?: User;
}

// ============ PERMISSIONS ============

export interface Permissions {
  create_contract: boolean;
  edit_all_contracts: boolean;
  view_financials: boolean;
  approve_contracts: boolean;
  export_data: boolean;
  manage_users: boolean;
  manage_integrations: boolean;
  manage_templates: boolean;
  manage_workflows: boolean;
  view_audit_logs: boolean;
}

export const DEFAULT_PERMISSIONS: Record<UserRole, Permissions> = {
  admin: {
    create_contract: true,
    edit_all_contracts: true,
    view_financials: true,
    approve_contracts: true,
    export_data: true,
    manage_users: true,
    manage_integrations: true,
    manage_templates: true,
    manage_workflows: true,
    view_audit_logs: true,
  },
  legal: {
    create_contract: true,
    edit_all_contracts: true,
    view_financials: true,
    approve_contracts: true,
    export_data: true,
    manage_users: false,
    manage_integrations: false,
    manage_templates: true,
    manage_workflows: false,
    view_audit_logs: true,
  },
  requester: {
    create_contract: true,
    edit_all_contracts: false,
    view_financials: false,
    approve_contracts: false,
    export_data: false,
    manage_users: false,
    manage_integrations: false,
    manage_templates: false,
    manage_workflows: false,
    view_audit_logs: false,
  },
  approver: {
    create_contract: false,
    edit_all_contracts: false,
    view_financials: true,
    approve_contracts: true,
    export_data: false,
    manage_users: false,
    manage_integrations: false,
    manage_templates: false,
    manage_workflows: false,
    view_audit_logs: false,
  },
  viewer: {
    create_contract: false,
    edit_all_contracts: false,
    view_financials: false,
    approve_contracts: false,
    export_data: false,
    manage_users: false,
    manage_integrations: false,
    manage_templates: false,
    manage_workflows: false,
    view_audit_logs: false,
  },
};
